// Patient Community Forum Component
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  MessageCircle, Heart, Eye, Search, Plus, User, Clock, 
  ThumbsUp, Send, AlertTriangle, Loader2, ArrowLeft, Tag,
  MessageSquare, TrendingUp, Users, Shield
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_ICONS = {
  general: MessageCircle,
  wellness: Heart,
  nutrition: Shield,
  fitness: TrendingUp,
  mental_health: Users,
  chronic_illness: Shield,
  pregnancy: Heart,
  pediatrics: Users,
  senior_health: Users,
  questions: MessageSquare
};

const ForumCommunity = ({ token, user }) => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // View states
  const [view, setView] = useState('list'); // 'list' | 'post' | 'create'
  const [selectedPost, setSelectedPost] = useState(null);
  const [postComments, setPostComments] = useState([]);
  
  // Create post state
  const [newPost, setNewPost] = useState({
    title: '',
    content: '',
    category: 'general',
    tags: '',
    anonymous: false
  });
  
  // Comment state
  const [newComment, setNewComment] = useState('');
  const [commentAnonymous, setCommentAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchCategories();
    fetchPosts();
  }, [selectedCategory, page]);

  const fetchCategories = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/forum/categories`);
      setCategories(res.data.categories || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (selectedCategory !== 'all') params.append('category', selectedCategory);
      if (searchTerm) params.append('search', searchTerm);
      params.append('page', page);
      params.append('limit', 15);

      const res = await axios.get(`${API_URL}/api/forum/posts?${params}`);
      setPosts(res.data.posts || []);
      setTotalPages(res.data.pages || 1);
    } catch (error) {
      console.error('Error fetching posts:', error);
      setPosts([]);
    }
    setLoading(false);
  };

  const fetchPost = async (postId) => {
    try {
      const res = await axios.get(`${API_URL}/api/forum/posts/${postId}`);
      setSelectedPost(res.data.post);
      setPostComments(res.data.comments || []);
      setView('post');
    } catch (error) {
      toast.error('Erreur lors du chargement du post');
    }
  };

  const handleCreatePost = async () => {
    if (!token) {
      toast.error('Connectez-vous pour publier');
      return;
    }
    if (!newPost.title.trim() || !newPost.content.trim()) {
      toast.error('Titre et contenu requis');
      return;
    }

    setSubmitting(true);
    try {
      const tags = newPost.tags.split(',').map(t => t.trim()).filter(t => t);
      await axios.post(`${API_URL}/api/forum/posts`, {
        ...newPost,
        tags
      }, { headers });
      
      toast.success('Post publié !');
      setNewPost({ title: '', content: '', category: 'general', tags: '', anonymous: false });
      setView('list');
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la publication');
    }
    setSubmitting(false);
  };

  const handleAddComment = async () => {
    if (!token) {
      toast.error('Connectez-vous pour commenter');
      return;
    }
    if (!newComment.trim()) return;

    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/forum/posts/${selectedPost.id}/comments`, {
        content: newComment,
        anonymous: commentAnonymous
      }, { headers });
      
      setPostComments([...postComments, res.data]);
      setNewComment('');
      toast.success('Commentaire ajouté');
    } catch (error) {
      toast.error('Erreur lors de l\'ajout du commentaire');
    }
    setSubmitting(false);
  };

  const handleLikePost = async (postId) => {
    if (!token) {
      toast.error('Connectez-vous pour aimer');
      return;
    }
    try {
      const res = await axios.post(`${API_URL}/api/forum/posts/${postId}/like`, {}, { headers });
      
      // Update local state
      if (selectedPost?.id === postId) {
        setSelectedPost({
          ...selectedPost,
          likes: selectedPost.likes + (res.data.liked ? 1 : -1)
        });
      }
      setPosts(posts.map(p => 
        p.id === postId ? { ...p, likes: p.likes + (res.data.liked ? 1 : -1) } : p
      ));
    } catch (error) {
      toast.error('Erreur');
    }
  };

  const getCategoryInfo = (catId) => {
    return categories.find(c => c.id === catId) || { name: { fr: catId }, color: '#6366f1' };
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60000) return 'À l\'instant';
    if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Il y a ${Math.floor(diff / 3600000)}h`;
    return date.toLocaleDateString('fr');
  };

  // ==================== RENDER LIST VIEW ====================
  if (view === 'list') {
    return (
      <div className="space-y-6" data-testid="forum-community">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h2 className="text-2xl font-bold text-white flex items-center gap-2">
              <MessageCircle className="w-6 h-6 text-blue-400" />
              Forum Communauté
            </h2>
            <p className="text-slate-400 text-sm">Échangez avec d'autres patients</p>
          </div>
          {token && (
            <Button onClick={() => setView('create')} className="bg-blue-600 hover:bg-blue-700">
              <Plus className="w-4 h-4 mr-2" />
              Nouveau Post
            </Button>
          )}
        </div>

        {/* Filters */}
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Rechercher..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && fetchPosts()}
              className="pl-10 bg-slate-700 border-slate-600 text-white"
            />
          </div>
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-full sm:w-48 bg-slate-700 border-slate-600 text-white">
              <SelectValue placeholder="Catégorie" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="all" className="text-white">Toutes les catégories</SelectItem>
              {categories.map(cat => (
                <SelectItem key={cat.id} value={cat.id} className="text-white">
                  {cat.name.fr}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Category Pills */}
        <div className="flex flex-wrap gap-2">
          {categories.slice(0, 6).map(cat => {
            const Icon = CATEGORY_ICONS[cat.id] || MessageCircle;
            return (
              <button
                key={cat.id}
                onClick={() => setSelectedCategory(cat.id === selectedCategory ? 'all' : cat.id)}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm transition-colors ${
                  selectedCategory === cat.id
                    ? 'bg-blue-600 text-white'
                    : 'bg-slate-700 text-slate-300 hover:bg-slate-600'
                }`}
              >
                <Icon className="w-4 h-4" />
                {cat.name.fr}
              </button>
            );
          })}
        </div>

        {/* Posts List */}
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
          </div>
        ) : posts.length === 0 ? (
          <Card className="bg-slate-800/50 border-slate-700">
            <CardContent className="p-12 text-center">
              <MessageCircle className="w-16 h-16 mx-auto mb-4 text-slate-600" />
              <h3 className="text-white text-lg font-medium mb-2">Aucun post trouvé</h3>
              <p className="text-slate-400 text-sm">Soyez le premier à poster dans cette catégorie !</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {posts.map(post => {
              const catInfo = getCategoryInfo(post.category);
              return (
                <Card 
                  key={post.id} 
                  className="bg-slate-800/50 border-slate-700 hover:border-slate-600 cursor-pointer transition-colors"
                  onClick={() => fetchPost(post.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-4">
                      <div 
                        className="w-10 h-10 rounded-full flex items-center justify-center text-white text-sm font-bold"
                        style={{ backgroundColor: catInfo.color + '40' }}
                      >
                        {post.author_name[0]}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="text-white font-medium truncate">{post.author_name}</span>
                          <Badge 
                            className="text-xs"
                            style={{ backgroundColor: catInfo.color + '30', color: catInfo.color }}
                          >
                            {catInfo.name.fr}
                          </Badge>
                        </div>
                        <h3 className="text-white font-semibold mb-1 line-clamp-1">{post.title}</h3>
                        <p className="text-slate-400 text-sm line-clamp-2">{post.content}</p>
                        <div className="flex items-center gap-4 mt-3 text-slate-500 text-xs">
                          <span className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            {formatDate(post.created_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {post.views || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <ThumbsUp className="w-3 h-3" />
                            {post.likes || 0}
                          </span>
                          <span className="flex items-center gap-1">
                            <MessageSquare className="w-3 h-3" />
                            {post.comments_count || 0}
                          </span>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex justify-center gap-2">
            <Button 
              variant="outline" 
              disabled={page === 1}
              onClick={() => setPage(p => p - 1)}
            >
              Précédent
            </Button>
            <span className="flex items-center px-4 text-slate-400">
              Page {page} / {totalPages}
            </span>
            <Button 
              variant="outline" 
              disabled={page === totalPages}
              onClick={() => setPage(p => p + 1)}
            >
              Suivant
            </Button>
          </div>
        )}
      </div>
    );
  }

  // ==================== RENDER POST VIEW ====================
  if (view === 'post' && selectedPost) {
    const catInfo = getCategoryInfo(selectedPost.category);
    return (
      <div className="space-y-6" data-testid="forum-post-view">
        <Button variant="ghost" onClick={() => setView('list')} className="text-slate-400">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour au forum
        </Button>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardContent className="p-6">
            <div className="flex items-start gap-4 mb-4">
              <div 
                className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold"
                style={{ backgroundColor: catInfo.color + '40' }}
              >
                {selectedPost.author_name[0]}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="text-white font-medium">{selectedPost.author_name}</span>
                  <Badge style={{ backgroundColor: catInfo.color + '30', color: catInfo.color }}>
                    {catInfo.name.fr}
                  </Badge>
                </div>
                <span className="text-slate-500 text-sm">{formatDate(selectedPost.created_at)}</span>
              </div>
            </div>

            <h1 className="text-2xl font-bold text-white mb-4">{selectedPost.title}</h1>
            <p className="text-slate-300 whitespace-pre-wrap">{selectedPost.content}</p>

            {selectedPost.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 mt-4">
                {selectedPost.tags.map((tag, i) => (
                  <Badge key={i} variant="outline" className="text-slate-400">
                    <Tag className="w-3 h-3 mr-1" />
                    {tag}
                  </Badge>
                ))}
              </div>
            )}

            <div className="flex items-center gap-4 mt-6 pt-4 border-t border-slate-700">
              <Button 
                variant="ghost" 
                onClick={() => handleLikePost(selectedPost.id)}
                className="text-slate-400 hover:text-red-400"
              >
                <ThumbsUp className="w-4 h-4 mr-2" />
                {selectedPost.likes || 0} J'aime
              </Button>
              <span className="text-slate-500 text-sm">
                <Eye className="w-4 h-4 inline mr-1" />
                {selectedPost.views || 0} vues
              </span>
            </div>
          </CardContent>
        </Card>

        {/* Comments */}
        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5" />
              Commentaires ({postComments.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {postComments.map(comment => (
              <div key={comment.id} className="p-4 bg-slate-700/50 rounded-lg">
                <div className="flex items-center gap-2 mb-2">
                  <span className="text-white font-medium">{comment.author_name}</span>
                  <span className="text-slate-500 text-xs">{formatDate(comment.created_at)}</span>
                </div>
                <p className="text-slate-300">{comment.content}</p>
              </div>
            ))}

            {token && (
              <div className="pt-4 border-t border-slate-700">
                <Textarea
                  placeholder="Écrire un commentaire..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white mb-3"
                />
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Switch checked={commentAnonymous} onCheckedChange={setCommentAnonymous} />
                    <Label className="text-slate-400 text-sm">Anonyme</Label>
                  </div>
                  <Button onClick={handleAddComment} disabled={submitting}>
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                    Commenter
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  // ==================== RENDER CREATE VIEW ====================
  if (view === 'create') {
    return (
      <div className="space-y-6" data-testid="forum-create-post">
        <Button variant="ghost" onClick={() => setView('list')} className="text-slate-400">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Retour au forum
        </Button>

        <Card className="bg-slate-800/50 border-slate-700">
          <CardHeader>
            <CardTitle className="text-white">Créer un nouveau post</CardTitle>
            <CardDescription>Partagez votre expérience avec la communauté</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-slate-400">Titre *</Label>
              <Input
                value={newPost.title}
                onChange={(e) => setNewPost({...newPost, title: e.target.value})}
                placeholder="Titre de votre post..."
                className="bg-slate-700 border-slate-600 text-white"
                maxLength={200}
              />
            </div>

            <div>
              <Label className="text-slate-400">Catégorie</Label>
              <Select value={newPost.category} onValueChange={(v) => setNewPost({...newPost, category: v})}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id} className="text-white">
                      {cat.name.fr}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label className="text-slate-400">Contenu *</Label>
              <Textarea
                value={newPost.content}
                onChange={(e) => setNewPost({...newPost, content: e.target.value})}
                placeholder="Partagez votre question ou expérience..."
                className="bg-slate-700 border-slate-600 text-white min-h-[200px]"
                maxLength={5000}
              />
              <p className="text-xs text-slate-500 mt-1">{newPost.content.length}/5000</p>
            </div>

            <div>
              <Label className="text-slate-400">Tags (séparés par des virgules)</Label>
              <Input
                value={newPost.tags}
                onChange={(e) => setNewPost({...newPost, tags: e.target.value})}
                placeholder="santé, conseils, question..."
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>

            <div className="flex items-center gap-3 p-3 bg-slate-700/50 rounded-lg">
              <Switch checked={newPost.anonymous} onCheckedChange={(c) => setNewPost({...newPost, anonymous: c})} />
              <div>
                <Label className="text-white">Publier anonymement</Label>
                <p className="text-xs text-slate-400">Votre nom ne sera pas affiché</p>
              </div>
            </div>

            <div className="p-3 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
              <p className="text-yellow-400 text-sm flex items-center gap-2">
                <AlertTriangle className="w-4 h-4" />
                Rappel important
              </p>
              <p className="text-yellow-300/70 text-xs mt-1">
                Ce forum est un espace d'échange entre patients. Pour tout avis médical, consultez un professionnel de santé.
              </p>
            </div>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setView('list')} className="flex-1">
                Annuler
              </Button>
              <Button onClick={handleCreatePost} disabled={submitting} className="flex-1 bg-blue-600">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Publier
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return null;
};

export default ForumCommunity;
